import React from 'react';
import { NumberInput, UserUnitPriceInput, LengthInput, Tooltip } from 'components/common';
import DataSourcePriceInput from './DataSourcePriceInput';

// Inline SVGs (FontAwesome layer-group / plus) rather than <i class="fa ...">, because the vendored
// FontAwesome served site-wide predates fa-clone (the layers glyph) and renders nothing for it. Keyed by
// the group_icon token in settings.yml.
const TIER_ICONS = {
  layers: {
    viewBox: '0 0 512 512',
    path: 'M12.41 148.02l232.94 105.67c6.8 3.09 14.49 3.09 21.29 0l232.94-105.67c16.55-7.51 16.55-32.52 0-40.03L266.65 2.31a25.607 25.607 0 0 0-21.29 0L12.41 107.98c-16.55 7.51-16.55 32.53 0 40.04zm487.18 88.28l-58.09-26.33-161.64 73.27c-7.56 3.43-15.59 5.17-23.86 5.17s-16.29-1.74-23.86-5.17L70.51 209.97l-58.1 26.33c-16.55 7.5-16.55 32.5 0 40l232.94 105.59c6.8 3.08 14.49 3.08 21.29 0L499.59 276.3c16.55-7.5 16.55-32.5 0-40zm0 127.8l-57.87-26.23-161.86 73.37c-7.56 3.43-15.59 5.17-23.86 5.17s-16.29-1.74-23.86-5.17L70.29 337.87 12.41 364.1c-16.55 7.5-16.55 32.5 0 40l232.94 105.59c6.8 3.08 14.49 3.08 21.29 0L499.59 404.1c16.55-7.5 16.55-32.5 0-40z',
  },
  plus: {
    viewBox: '0 0 448 512',
    path: 'M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z',
  },
  arrows: {
    viewBox: '0 0 512 512',
    path: 'M377.941 169.941V216H134.059v-46.059c0-21.382-25.851-32.09-40.971-16.971L7.029 239.029c-9.373 9.373-9.373 24.569 0 33.941l86.059 86.059c15.119 15.119 40.971 4.411 40.971-16.971V296h243.882v46.059c0 21.382 25.851 32.09 40.971 16.971l86.059-86.059c9.373-9.373 9.373-24.569 0-33.941l-86.059-86.059c-15.119-15.12-40.971-4.412-40.971 16.97z',
  },
  arrows_v: {
    viewBox: '0 0 256 512',
    path: 'M214.059 377.941H168V134.059h46.059c21.382 0 32.09-25.851 16.971-40.971L144.971 7.029c-9.373-9.373-24.569-9.373-33.941 0L24.971 93.088c-15.119 15.119-4.411 40.971 16.971 40.971H88v243.882H41.941c-21.382 0-32.09 25.851-16.971 40.971l86.059 86.059c9.373 9.373 24.569 9.373 33.941 0l86.059-86.059c15.12-15.119 4.412-40.971-16.97-40.971z',
  },
};

// Renders one pricing tier (a run of fields sharing the same `group` in settings.yml) as a single
// card with its count / price / wastage inputs on one row. The fields keep their own names, so they
// still register, save, and feed the pricing formulas exactly as the standalone rows did -- only the
// presentation is grouped. QuoteBuilderForm coalesces the run and hands it here as `fields`.
const PricingThresholdCard = ({
  groupTitle,
  groupDescription,
  groupIcon,
  groupLayout,
  fields,
  values,
  setValue,
  register,
  errors,
  disabled,
  currency,
  dataSourcesEnabled,
  userUnitPriceId
}) => {

  const renderInput = (field) => {
    if (field.form_type == 'price') {
      const dataSourceConfigKey = `${field.name}_data_source_config`;
      const dataSourceConfig = values[dataSourceConfigKey];

      return (
        <>
          <UserUnitPriceInput
            name={field.name}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            register={register}
            errors={errors}
            value={values[field.name]}
            class_name={field.class_name}
            validators={field.validators != undefined ? field.validators : []}
            currency={currency}
            precision={field.precision}
            onChange={(e) => setValue(field.name, e.target.value)}
            readOnly={dataSourceConfig && dataSourceConfig.data_source_id}
          />
          {dataSourcesEnabled && field.data_source_enabled !== false && (
            <DataSourcePriceInput
              value={values[field.name]}
              onChange={(e) => setValue(field.name, e.target.value)}
              currency={currency}
              precision={field.precision}
              dataSourceConfig={dataSourceConfig}
              onDataSourceConfigChange={(newConfig) => setValue(dataSourceConfigKey, newConfig)}
              disabled={disabled}
              userUnitPriceId={userUnitPriceId}
              attributeName={field.name}
            />
          )}
        </>
      );
    }

    if (field.form_type == 'length') {
      // The unit (mm / in) lives in a sibling hidden field named by length_unit_in_values, exactly as the
      // standalone LengthFormField resolves it -- read it from the live values so the addon tracks the unit,
      // and translate it when the field flags length_unit_in_values_need_translate, mirroring LengthFormField.
      const rawUnit = field.length_unit_in_values != undefined ? values[field.length_unit_in_values] : undefined;
      const unit = field.length_unit_in_values_need_translate ? t('quote_builder.' + rawUnit) : rawUnit;

      return (
        <LengthInput
          name={field.name}
          placeholder={field.placeholder}
          register={register}
          errors={errors}
          unit={unit}
          precision={field.precision}
          value={values[field.name]}
          class_name={field.class_name}
          validators={field.validators != undefined ? field.validators : []}
          onChange={(e) => setValue(field.name, e.target.value)}
        />
      );
    }

    if (field.form_type == 'select') {
      // Native select mirroring SelectFormField, for the grouped card's dropdowns (the thickness unit, and the
      // gsm min/max variants). Bumping updated_at on change is what SelectFormField does so the form's change
      // detection / post-on-change fire -- and, crucially here, so the card re-renders and swaps the length vs
      // gsm min/max variants when the unit changes.
      return (
        <select
          name={field.name}
          value={values[field.name]}
          className={`${errors[field.name] ? 'input-error form-control general-frm-element' : 'form-control general-frm-element'}${field.class_name != undefined ? ' ' + field.class_name : ''}`}
          {...register(field.name, {
            onChange: (e) => { setValue(field.name, e.target.value); setValue('updated_at', Date.now()); },
          })}
        >
          {(field.values || []).map((valuePair) => (
            <option key={valuePair.value} value={valuePair.value}>{t(valuePair.label)}</option>
          ))}
        </select>
      );
    }

    return (
      <NumberInput
        name={field.name}
        placeholder={field.placeholder}
        register={register}
        errors={errors}
        class_name={field.class_name}
        integerOnly={true}
        validators={field.validators != undefined ? field.validators : []}
        value={values[field.name]}
        onChange={(e) => setValue(field.name, e.target.value)}
      />
    );
  };

  // A wastage_count field holds a single count whose unit -- sheets or products -- is fixed by the machine
  // type and stored on the record as wastage_unit. Read that live value so the label states the actual unit
  // the count is in ("Wastage (sheets)") rather than a bare "(count)". The flat make-ready wastage fields
  // carry their unit in their own name (wastage_count_in_sheets / _in_products) and are ungrouped, so they
  // never reach this card; a new record with no wastage_unit yet shows the bare label until it is saved.
  const labelFor = (field) => {
    const base = t(field.label);
    if (field.name == undefined || !field.name.endsWith('_wastage_count')) { return base; }

    const wastageUnit = values['wastage_unit'];
    if (!wastageUnit) { return base; }

    return `${base} (${t(`quote_builder.wastage_unit_${wastageUnit}`).toLowerCase()})`;
  };

  // Renders only the island div. The enclosing table row + cell (.pricing-threshold-card-cell) is owned by
  // QuoteBuilderForm#renderAttributes, which wraps every consecutive tier card in ONE row so the pair shares
  // a single zebra stripe and reads as one unit.
  return (
        <div className="pricing-threshold-card">
          <div className={`pricing-threshold-card__body${groupLayout == 'grid' ? ' pricing-threshold-card__body--split' : ''}`}>
          <div className="pricing-threshold-card__head">
            <span className="pricing-threshold-card__title">
              { groupIcon != undefined && (
                TIER_ICONS[groupIcon] != undefined ? (
                  <svg className="pricing-threshold-card__icon" viewBox={TIER_ICONS[groupIcon].viewBox} width="16" height="16" fill="currentColor" aria-hidden="true">
                    <path d={TIER_ICONS[groupIcon].path} />
                  </svg>
                ) : (
                  <i className={`fa ${groupIcon} pricing-threshold-card__icon`} aria-hidden="true"></i>
                )
              ) }
              {t(groupTitle)}
            </span>
            {groupDescription != undefined && (
              <span className="pricing-threshold-card__description">{t(groupDescription)}</span>
            )}
          </div>
          <div className={`pricing-threshold-card__fields${groupLayout == 'grid' ? ' pricing-threshold-card__fields--grid' : ''}`}>
            {fields.map((field) => (
              <div className="pricing-threshold-card__field" key={field.name}>
                <label className="pricing-threshold-card__label">
                  {labelFor(field)}
                  { field.tooltip_text != undefined && <Tooltip title={t(field.tooltip_text)} /> }
                </label>
                {renderInput(field)}
              </div>
            ))}
          </div>
          </div>
        </div>
  );
};

export default PricingThresholdCard;
